<template>
  <div
    v-if="(user && config) || (isDemo && config)"
    class="relative"
  >
    <Sidebar />
    <div
      class="min-h-screen overflow-hidden bg-slate-50 dark:bg-slate-900"
      :class="[!route.meta?.fullScreen ? 'lg:pl-72' : 'lg:pl-16']"
    >
      <div
        class="space-y-12"
        :class="[
          route.meta?.fullScreen
            ? ''
            : 'mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8'
        ]"
      >
        <router-view />
      </div>
    </div>

    <!-- Lazy-loaded Command Palette (Cmd+K) -->
    <CommandPalette
      v-if="commandPaletteOpen"
      v-model:open="commandPaletteOpen"
    />

    <!-- Lazy-loaded Righty AI Assistant -->
    <template v-if="!isDemo">
      <RightyContextPrompts />
      <RightyWindow />
    </template>

    <!-- Account Overdue Modals -->
    <PaymentFailedModal v-if="showPaymentFailedModal" />
    <Upgrade
      v-if="showTrialExpiredModal"
      :closeable="false"
      :is-blocking="true"
    />
  </div>
  <div
    v-else-if="isError"
    class="flex h-screen items-center justify-center"
  >
    <div class="text-center">
      <h1 class="text-2xl font-bold">
        There's an error loading your team's configuration.
      </h1>
      <p class="text-gray-500">
        Please contact support at support@rightmessage.com
      </p>
      <div class="mt-4">
        <TeamDropdown class="w-3xl" />
      </div>
    </div>
  </div>

  <!-- Loading state - matches inline loader for seamless transition -->
  <div
    v-else
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900"
  >
    <svg
      class="h-12 w-12 animate-spin text-slate-700 dark:text-slate-200"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
    <p class="mt-4 text-sm text-slate-500">
      Loading RightMessage...
    </p>
  </div>
</template>

<script setup lang="ts">
import { useRightyKeyboardShortcuts } from '@/modules/righty'
import { useSubscriptionStore } from '@/stores/subscriptionStore'

// Lazy-load heavy components to reduce initial bundle size
const RightyWindow = defineAsyncComponent(() =>
  import('@/modules/righty/components/RightyWindow.vue')
)
const RightyContextPrompts = defineAsyncComponent(() =>
  import('@/modules/righty/components/RightyContextPrompts.vue')
)
const CommandPalette = defineAsyncComponent(() =>
  import('@/components/common/CommandPalette.vue')
)
const PaymentFailedModal = defineAsyncComponent(() =>
  import('@/components/common/PaymentFailedModal.vue')
)
const Upgrade = defineAsyncComponent(() =>
  import('@/modules/settings/pages/Upgrade.vue')
)

const route = useRoute()

// Initialize Righty keyboard shortcuts (Cmd+J to open, Escape to close)
useRightyKeyboardShortcuts()

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const configStore = useConfigStore()
const { config, currentTeamPid, isError } = storeToRefs(configStore)
const { canAccess } = usePermissionStore()
provide('canAccess', canAccess)

useTeamStore()

const { identifyUser, setTeamGroup } = usePostHog()

const { isOpen: commandPaletteOpen } = useCommandPalette()

// Check if we're in demo mode
const isDemo = computed(() => route.params.teamPid === 'demo')

// Provide isDemo context for components that need to know if we're in demo mode
provide('isDemo', isDemo)

// Subscription state for overdue account modals
const subscriptionStore = useSubscriptionStore()
const { isTrialExpired, isPaymentFailed } = storeToRefs(subscriptionStore)

// Show payment failed modal (takes priority over trial expired)
const showPaymentFailedModal = computed(() => {
  if (isDemo.value) return false
  return isPaymentFailed.value
})

// Show trial expired modal (only if payment failed modal isn't showing)
const showTrialExpiredModal = computed(() => {
  if (isDemo.value) return false
  if (showPaymentFailedModal.value) return false
  return isTrialExpired.value
})

const { currentTeam } = storeToRefs(configStore)

// Identify user to PostHog when authenticated
watch(
  user,
  (newUser) => {
    if (newUser && !isDemo.value) {
      identifyUser({
        id: newUser.id,
        email: newUser.email,
        name: newUser.name,
        created_at: newUser.created_at,
        trial_ends_at: newUser.trial_ends_at,
        free_account: newUser.free_account,
        on_trial: newUser.on_trial,
        current_billing_plan: newUser.current_billing_plan,
        stripe_status: newUser.stripe_status,
        active_subscription: newUser.active_subscription,
        is_agency: newUser.is_agency,
        first_promoter_ref_id: newUser.first_promoter_ref_id,
        owned_teams: newUser.owned_teams
      })
    }
  },
  { immediate: true }
)

// Set team group context when team changes
watch(
  currentTeam,
  (team) => {
    if (team && !isDemo.value) {
      setTeamGroup({
        pid: team.pid,
        name: team.name,
        current_billing_plan: team.current_billing_plan
      })
    }
  },
  { immediate: true }
)

watch(
  () => route.params.teamPid,
  () => {
    const teamPid = route.params.teamPid as string
    // Handle demo mode - keep as string 'demo', otherwise parse as integer
    currentTeamPid.value = teamPid === 'demo' ? 'demo' : parseInt(teamPid)
  },
  { immediate: true }
)
</script>
